import {
    LinkGeneralInterface,
    LinkInterface,
} from 'components/interfaces/GeneralInterface';
import { getRoute } from 'constants/routes';
import { Config_Person_PageMenu_ApiEnum } from 'submodules/api_middleware';
import { TNamespaceEnum } from 'types/types';
import { getFilteredLinks } from 'utils/helpers/links';
import { getTranslationKey } from 'utils/localization';

/**
 * Per-viewer/per-profile flags evaluated in `PersonBase` and forwarded to the
 * link builder. They drive role-based visibility of menu items that are not
 * gated by the backend `pageMenu` (e.g. the IS1-hosted Finance and
 * Správa soutěží items added in May 2026).
 */
export interface PersonLinksFlags {
    /** Viewer is looking at their OWN profile. */
    isMe: boolean;
    /** Viewer holds the referee or delegate role (IS1: web_rozhodci / web_delegati). */
    viewerIsRefOrDel: boolean;
}

const EMPTY_FLAGS: PersonLinksFlags = {
    isMe: false,
    viewerIsRefOrDel: false,
};

interface Props {
    idx: Config_Person_PageMenu_ApiEnum;
    text: string;
    link: string;
    blankPage?: boolean;
    items?: LinkInterface<Config_Person_PageMenu_ApiEnum>[];
}

const createLink = ({
    idx,
    text,
    link,
    blankPage,
    items,
}: Props): LinkGeneralInterface<Config_Person_PageMenu_ApiEnum> => ({
    idx,
    text,
    link,
    blankPage,
    items,
});

const getLinksInner = (
    id: number,
    agentId?: number,
    externalId?: string,
    flags: PersonLinksFlags = EMPTY_FLAGS
): LinkGeneralInterface<Config_Person_PageMenu_ApiEnum>[] => [
    createLink({
        idx: Config_Person_PageMenu_ApiEnum['person-detail'],
        text: 'pages.overview',
        link: getRoute('/members/{0}', ['' + id]),
        items: [
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.personDetails',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/hraci/osoby-detail/?req=${externalId.toLowerCase()}&sport=fotbal&ia=1`
                    : undefined,
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.passport',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/hraci/hraci-passport/?req=${externalId.toLowerCase()}&sport=fotbal&ia=1`
                    : undefined,
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.playerMatches',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/hraci/hraci-zapasy/?req=${externalId.toLowerCase()}&sport=fotbal&ia=1`
                    : undefined,
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.decisionsStkDk',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/hraci/rozhodnuti-stk-dk/?req=${externalId.toLowerCase()}&sport=fotbal`
                    : undefined,
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-absences'],
                text: 'pages.absences',
                link: getRoute('/members/{0}/absences', ['' + id]),
            },
            // {
            //     idx: Config_Person_PageMenu_ApiEnum['user-detail'],
            //     text: 'pages.userAccess',
            //     link: getRoute('/members/{0}/user_access', ['' + id]),
            // },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.marketing',
                link: getRoute('/members/{0}/details', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.photos',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/hraci/foto/?req=${externalId.toLowerCase()}&sport=fotbal`
                    : undefined,
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['member-requests'],
                text: 'pages.requests',
                link: getRoute('/members/{0}/requests', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['member-invoice-account'],
                text: 'pages.accountsTransactions',
                link: getRoute('/members/{0}/accounts/transactions', ['' + id]),
            },
            // TODO: after API uncomment
            // {
            //     idx: Config_Person_PageMenu_ApiEnum['member-penalization'],
            //     text: 'pages.penalizations',
            //     link: getRoute('/members/{0}/penalization', ['' + id]),
            // },
            {
                idx: Config_Person_PageMenu_ApiEnum['member-applications'],
                text: 'pages.applications',
                link: getRoute('/members/{0}/applications', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['person-audit-log'],
                text: 'pages.auditLog',
                link: getRoute('/members/{0}/audit-log', ['' + id]),
            },
        ],
    }),
    createLink({
        idx: undefined,
        text: 'pages.finances',
        link: undefined,
        items: [
            {
                idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                text: 'pages.memberPayments',
                link: externalId
                    ? `${
                          process.env.NEXT_PUBLIC_IS1_URL
                      }/finance/platby-clena/?req=${externalId.toLowerCase()}&sport=fotbal`
                    : undefined,
            },
            ...(flags.isMe && flags.viewerIsRefOrDel
                ? [
                      {
                          idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                          text: 'pages.personCollectionAccount',
                          link: `${process.env.NEXT_PUBLIC_IS1_URL}/finance/sberny-ucet-osoby/?sport=fotbal`,
                      },
                      {
                          idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                          text: 'pages.personInvoices',
                          link: `${process.env.NEXT_PUBLIC_IS1_URL}/finance/faktury-osoby/?sport=fotbal`,
                      },
                  ]
                : []),
            {
                idx: Config_Person_PageMenu_ApiEnum['person-billing-fields'],
                text: 'pages.personBillingFields',
                link: getRoute('/members/{0}/billing', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum[
                    'person-account-transactions'
                ],
                text: 'pages.accountsTransactions',
                link: getRoute('/members/{0}/accounts/transactions', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum[
                    'person-account-rewards-documents'
                ],
                text: 'pages.personAccountsRewardsInvoices',
                link: getRoute('/members/{0}/accounts/rewards', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum[
                    'person-account-issued-documents'
                ],
                text: 'pages.personAccountsIssuedInvoices',
                link: getRoute('/members/{0}/accounts/issued', ['' + id]),
            },
        ],
    }),
    ...(flags.viewerIsRefOrDel
        ? [
              createLink({
                  idx: undefined,
                  text: 'pages.competitionsManagement',
                  link: undefined,
                  items: [
                      {
                          idx: Config_Person_PageMenu_ApiEnum['person-fields'],
                          text: 'pages.refereeMatches',
                          link: `${process.env.NEXT_PUBLIC_IS1_URL}/rozhodci/zapasy-rozhodciho/?sport=fotbal`,
                      },
                  ],
              }),
          ]
        : []),
    createLink({
        idx: Config_Person_PageMenu_ApiEnum['player-detail'],
        text: getTranslationKey(TNamespaceEnum.person, 'role.player'),
        link: getRoute('/players/{0}', ['' + id]),
        items: [
            {
                idx: Config_Person_PageMenu_ApiEnum['player-contracts'],
                text: 'pages.contracts',
                link: getRoute('/players/{0}/contracts', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['player-matches'],
                text: 'pages.matches',
                link: getRoute('/players/{0}/matches', ['' + id]),
            },
        ],
    }),
    createLink({
        idx: Config_Person_PageMenu_ApiEnum['referee-detail'],
        text: getTranslationKey(TNamespaceEnum.person, 'role.referee'),
        link: getRoute('/referees/{0}', ['' + id]),
        items: [
            {
                idx: Config_Person_PageMenu_ApiEnum['referee-licenses'],
                text: 'MemberProfile.licence',
                link: getRoute('/referees/{0}/licences', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['referee-matches'],
                text: 'pages.matches',
                link: getRoute('/referees/{0}/matches', ['' + id]),
            },
        ],
    }),
    // Coaches module is IS1-only and routes by Osoba.PublishId, so prefer the
    // person's IS1 publish_id when available; fall back to numeric id only as a
    // last resort (which would 404 — the BE no longer accepts integer routes).
    //
    // Sidebar mirrors the member detail (Přehled → Osobní údaje / Utkání).
    // Licence overview AND career history (Is1TrenerOddil) both render inline
    // on Osobní údaje as Passport-style blocks, so the standalone /licences and
    // /clubs subpages are gone.
    ((): LinkGeneralInterface<Config_Person_PageMenu_ApiEnum> => {
        const coachId = externalId ?? '' + id;
        return createLink({
            idx: Config_Person_PageMenu_ApiEnum['coach-detail'],
            text: 'pages.overview',
            link: getRoute('/coaches/{0}', [coachId]),
            items: [
                {
                    idx: Config_Person_PageMenu_ApiEnum['coach-detail'],
                    text: 'pages.personDetails',
                    link: getRoute('/coaches/{0}', [coachId]),
                },
                {
                    idx: Config_Person_PageMenu_ApiEnum['coach-matches'],
                    text: 'pages.matches',
                    link: getRoute('/coaches/{0}/matches', [coachId]),
                },
            ],
        });
    })(),
    createLink({
        idx: Config_Person_PageMenu_ApiEnum['delegate-detail'],
        text: getTranslationKey(TNamespaceEnum.person, 'role.delegate'),
        link: getRoute('/delegates/{0}', ['' + id]),
        items: [
            {
                idx: Config_Person_PageMenu_ApiEnum['delegate-licenses'],
                text: 'MemberProfile.licence',
                link: getRoute('/delegates/{0}/licences', ['' + id]),
            },
            {
                idx: Config_Person_PageMenu_ApiEnum['delegate-matches'],
                text: 'pages.matches',
                link: getRoute('/delegates/{0}/matches', ['' + id]),
            },
        ],
    }),
    ...(agentId
        ? [
              createLink({
                  idx: Config_Person_PageMenu_ApiEnum['person-agent-detail'],
                  text: 'agent.agent_short',
                  link: getRoute('/agents/{0}', ['' + agentId]),
                  items: [
                      {
                          idx: Config_Person_PageMenu_ApiEnum[
                              'person-agent-contracts'
                          ],
                          text: 'pages.contracts',
                          link: getRoute('/agents/{0}/contracts', [
                              '' + agentId,
                          ]),
                      },
                      {
                          idx: Config_Person_PageMenu_ApiEnum[
                              'person-agent-insurances'
                          ],
                          text: 'pages.insurances',
                          link: getRoute('/agents/{0}/insurances', [
                              '' + agentId,
                          ]),
                      },
                  ],
              }),
          ]
        : []),
];

export const getLinks = (
    id: number,
    configInnerPageMenu: string[],
    agentId?: number,
    externalId?: string,
    flags: PersonLinksFlags = EMPTY_FLAGS
): LinkGeneralInterface<Config_Person_PageMenu_ApiEnum>[] =>
    getFilteredLinks(
        configInnerPageMenu,
        getLinksInner(id, agentId, externalId, flags)
    );
